import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useCreateClient, useUpdateClient } from '@/hooks/queries/useClients';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/components/ui/toast/useToast';
import { WILAYAS } from '@/lib/utils';
import type { Client } from '@/types/client';

const supplierSchema = z.object({
  name: z.string().min(1, 'الاسم مطلوب / Nom requis'),
  phone: z.string().min(1, 'رقم الهاتف مطلوب / Téléphone requis'),
  email: z.string().email('بريد إلكتروني غير صالح / Email invalide').optional().or(z.literal('')),
  address: z.string().optional(),
  wilaya: z.string().min(1, 'الولاية مطلوبة / Wilaya requise'),
  taxNumber: z.string().optional(),
  commercialRegister: z.string().optional(),
  nisNumber: z.string().optional(),
  articleNumber: z.string().optional(),
  creditLimit: z.number().min(0).optional(),
  notes: z.string().optional(),
});

type SupplierFormData = z.infer<typeof supplierSchema>;

interface SupplierFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier?: Client | null;
  onSuccess?: () => void;
}

export function SupplierForm({ open, onOpenChange, supplier, onSuccess }: SupplierFormProps) {
  const { company } = useAuthStore();
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<SupplierFormData>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      wilaya: 'Alger',
      creditLimit: 0,
    },
  });

  useEffect(() => {
    if (open) {
      if (supplier) {
        reset({
          name: supplier.name,
          phone: supplier.phone || '',
          email: supplier.email || '',
          address: supplier.address || '',
          wilaya: supplier.wilaya || 'Alger',
          taxNumber: supplier.tax_id || '',
          commercialRegister: supplier.nrc || '',
          nisNumber: supplier.nis || '',
          articleNumber: supplier.article_number || '',
          creditLimit: supplier.credit_limit || 0,
          notes: supplier.notes || '',
        });
      } else {
        reset({
          name: '',
          phone: '',
          email: '',
          address: '',
          wilaya: 'Alger',
          taxNumber: '',
          commercialRegister: '',
          nisNumber: '',
          articleNumber: '',
          creditLimit: 0,
          notes: '',
        });
      }
    }
  }, [open, supplier, reset]);

  const onSubmit = async (data: SupplierFormData) => {
    if (!company?.id) return;

    try {
      const supplierData: any = {
        company_id: company.id,
        name: data.name,
        type: 'supplier',
        phone: data.phone,
        email: data.email || null,
        address: data.address || null,
        wilaya: data.wilaya,
        tax_id: data.taxNumber || null,
        nrc: data.commercialRegister || null,
        nis: data.nisNumber || null,
        article_number: data.articleNumber || null,
        credit_limit: data.creditLimit || null,
        payment_terms: null,
        notes: data.notes || null,
      };

      if (supplier) {
        await updateClient.mutateAsync({ id: supplier.id, ...supplierData });
        toast.success(
          'تم التحديث بنجاح / Modifié avec succès',
          'تم تحديث المورد / Le fournisseur a été modifié'
        );
      } else {
        await createClient.mutateAsync(supplierData);
        toast.success(
          'تم الإضافة بنجاح / Ajouté avec succès',
          'تم إضافة المورد / Le fournisseur a été ajouté'
        );
      }

      onSuccess?.();
      onOpenChange(false);
      reset();
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error(
        'خطأ / Erreur',
        error instanceof Error ? error.message : 'فشل حفظ المورد'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {supplier
              ? 'تعديل المورد / Modifier le fournisseur'
              : 'مورد جديد / Nouveau fournisseur'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">
              المعلومات الأساسية / Informations de base
            </h3>

            <div className="space-y-2">
              <Label htmlFor="name">الاسم / Nom *</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="شركة التوريد / Société Approvisionnement"
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">رقم الهاتف / Téléphone *</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                  placeholder="0551234567"
                />
                {errors.phone && (
                  <p className="text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">البريد الإلكتروني / Email</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="contact@example.dz"
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">العنوان / Adresse</Label>
              <Input
                id="address"
                {...register('address')}
                placeholder="15 شارع ديدوش مراد / 15 Rue Didouche Mourad"
              />
            </div>

            <div className="space-y-2">
              <Label>الولاية / Wilaya *</Label>
              <Select
                value={watch('wilaya')}
                onValueChange={(value) => setValue('wilaya', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {WILAYAS.map((wilaya) => (
                    <SelectItem key={wilaya.code} value={wilaya.nameFr}>
                      {wilaya.code} - {wilaya.nameAr} / {wilaya.nameFr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.wilaya && (
                <p className="text-sm text-red-600">{errors.wilaya.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">
              المعلومات الجبائية / Informations fiscales
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="taxNumber">
                  رقم التعريف الجبائي / NIF
                  <span className="text-xs text-gray-500 ml-2">(15 chiffres)</span>
                </Label>
                <Input
                  id="taxNumber"
                  {...register('taxNumber')}
                  placeholder="123456789012345"
                  maxLength={15}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="commercialRegister">
                  السجل التجاري / RC
                  <span className="text-xs text-gray-500 ml-2">(ex: 01/00-1234567)</span>
                </Label>
                <Input
                  id="commercialRegister"
                  {...register('commercialRegister')}
                  placeholder="01/00-1234567"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nisNumber">
                  رقم التعريف الإحصائي / NIS
                </Label>
                <Input
                  id="nisNumber"
                  {...register('nisNumber')}
                  placeholder="123456789012345"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="articleNumber">
                  رقم المادة / AI
                </Label>
                <Input
                  id="articleNumber"
                  {...register('articleNumber')}
                  placeholder="12345678901234"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">
              المعلومات المالية / Informations financières
            </h3>

            <div className="space-y-2">
              <Label htmlFor="creditLimit">
                الحد الائتماني / Limite de crédit (DZD)
              </Label>
              <Input
                id="creditLimit"
                type="number"
                step="0.01"
                {...register('creditLimit', { valueAsNumber: true })}
                placeholder="0.00"
              />
              {errors.creditLimit && (
                <p className="text-sm text-red-600">{errors.creditLimit.message}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                الحد الأقصى للائتمان المسموح به / Montant maximum de crédit autorisé
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">ملاحظات / Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="ملاحظات إضافية / Notes supplémentaires..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              إلغاء / Annuler
            </Button>
            <Button
              type="submit"
              disabled={createClient.isPending || updateClient.isPending}
              className="bg-gradient-to-r from-orange-500 to-red-600"
            >
              {(createClient.isPending || updateClient.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {supplier ? 'حفظ / Enregistrer' : 'إضافة / Ajouter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}